import React, { useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  message,
} from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import type { KrybotTemplate } from '../../../types/krybot';
import styles from './TemplateManagement.module.css';

const { TextArea } = Input;

interface TemplateDetailsTabProps {
  template: KrybotTemplate | null;
  templateData: Partial<KrybotTemplate>;
  isCreating: boolean;
  onDataChange: (updates: Partial<KrybotTemplate>) => void;
  onSave: () => void;
  onCancel: () => void;
  loading: boolean;
}

export const TemplateDetailsTab: React.FC<TemplateDetailsTabProps> = ({
  template,
  templateData,
  isCreating,
  onDataChange,
  onSave,
  onCancel,
  loading,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      template_key: templateData.template_key || '',
      template_type: templateData.template_type || '',
      sector: templateData.sector || '',
      prompt_text: templateData.prompt_text || '',
      is_active: templateData.is_active ?? true,
    });
  }, [templateData, form]);

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    onDataChange(values);
  };

  return (
    <div className="template-details-tab">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Template Details</h3>
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            loading={loading}
          >
            {isCreating ? 'Create Template' : 'Save Changes'}
          </Button>
          {isCreating && (
            <Button icon={<CloseOutlined />} onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Space>
      </div>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={{
          template_key: templateData.template_key || '',
          template_type: templateData.template_type || '',
          sector: templateData.sector || '',
          prompt_text: templateData.prompt_text || '',
          is_active: templateData.is_active ?? true,
        }}
      >
        <Form.Item
          name="template_key"
          label="Template Key"
          rules={[{ required: true, message: 'Template key is required' }]}
        >
          <Input
            placeholder="e.g., chat.prime.intro"
            disabled={loading || (!!template && !isCreating)}
          />
        </Form.Item>

        <Form.Item
          name="template_type"
          label="Template Type"
          rules={[{ required: true, message: 'Template type is required' }]}
        >
          <Input
            placeholder="e.g., chat, system, user"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item name="sector" label="Sector (Optional)">
          <Input
            placeholder="e.g., finance, manufacturing"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="prompt_text"
          label="Prompt Text"
          rules={[{ required: true, message: 'Prompt text is required' }]}
        >
          <TextArea
            rows={12}
            placeholder="Enter the prompt template text. Use {{variableName}} for variables."
            disabled={loading}
          />
        </Form.Item>

        <Form.Item name="is_active" valuePropName="checked" label="Active">
          <Switch disabled={loading} />
        </Form.Item>
      </Form>
    </div>
  );
};

